import { Injectable } from '@angular/core';
import { WebServiceEndpoints } from '@common/web-service-endpoints';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { CognitoAuthResponse } from './cognito/cognito-auth.response';
import { ConfigService } from '@common/services/config/config.service';
import { QueryParams, stringifyQueryParams } from '@common/helpers/http-helper';
import { CognitoConstants } from './cognitoConstants';

export interface IAuthorizeResponse {
  ContactCenterToken: string;
}

export interface IGetUserTokenDataResponse {
  SpecialistId: string;
  UserName: string;
  FirstName: string;
  LastName: string;
  IsAdmin: boolean;
  IsEnrolledInDocuSign: boolean;
  DocusignUserId: string;
  IsSkillsInitialized: boolean;
}

export interface IRefreshTokenResponse {
  Token: string;
  TokenExpirationTimeoutInSeconds: number;
}

@Injectable()
export class AuthApiService {
  constructor(private readonly http: HttpClient, private readonly configService: ConfigService) {}

  public async apiAuthorize(authToken: string): Promise<IAuthorizeResponse> {
    return this.http
      .post<IAuthorizeResponse>(WebServiceEndpoints.Settings.authorize, {
        cognitoToken: authToken,
      })
      .toPromise();
  }

  public async getUserTokenData(ccToken: string): Promise<IGetUserTokenDataResponse> {
    const headers = new HttpHeaders({ 'x-tenant-id': this.configService.tenantId });
    return this.http
      .post<IGetUserTokenDataResponse>(
        WebServiceEndpoints.Settings.decryptCCToken,
        {
          Token: ccToken,
        },
        { headers },
      )
      .toPromise();
  }

  public async refreshCCToken(ccRefreshToken: string): Promise<IRefreshTokenResponse> {
    return this.http
      .post<IRefreshTokenResponse>(WebServiceEndpoints.Settings.refreshCCToken, {
        RefreshToken: ccRefreshToken,
      })
      .toPromise();
  }

  public async signOut(cognitoToken: string): Promise<void> {
    await this.http
      .post(WebServiceEndpoints.User.globalSignOut, {
        AccessToken: cognitoToken,
      })
      .toPromise();
  }

  public async requestToken(query: QueryParams): Promise<CognitoAuthResponse> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded',
      }),
    };

    const tokenUrl = this.composeCognitoUrl(CognitoConstants.endpoints.token);

    return this.http.post<CognitoAuthResponse>(tokenUrl, stringifyQueryParams(query), httpOptions).toPromise();
  }

  public composeCognitoUrl(endpointUri: string): string {
    const authSettings = this.configService.authSettings;
    const domain = authSettings.DomainPrefix;
    const region = authSettings.Region;
    const hostUrl = CognitoConstants.hostUrl;
    return `https://${domain}.auth.${region}.${hostUrl}/${endpointUri}`;
  }
}
